import axios from 'axios';
import { API_V1_URL } from '../../../config';

const api = axios.create({
  baseURL: API_V1_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors (token expired — but NOT during login/register)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const isAuthEndpoint = error.config?.url?.includes('/auth/student/');
    if (error.response?.status === 401 && !isAuthEndpoint) {
      const path = window.location.pathname || '';
      if (path === '/login' || path === '/register') {
        return Promise.reject(error);
      }
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      const next = `${path}${window.location.search || ''}`;
      const dest = next.startsWith('/assessments/')
        ? `/login?next=${encodeURIComponent(next)}`
        : '/login';
      window.location.href = dest;
    }
    return Promise.reject(error);
  }
);

// Student dashboard API
export const studentAPI = {
  getDashboard: () => api.get('/student/dashboard'),
};

// Student assessment API
export const assessmentAPI = {
  getDetail: (assessmentId) => api.get(`/student/assessments/${assessmentId}`),
  start: (assessmentId) => api.post(`/student/assessments/${assessmentId}/start`),
  createPayment: (assessmentId) => api.post(`/student/assessments/${assessmentId}/pay`),
  verifyPayment: (assessmentId, data) => api.post(`/student/assessments/${assessmentId}/pay/verify`, data),
  getAttempt: (attemptId) => api.get(`/student/attempts/${attemptId}`),
  getTimer: (attemptId) => api.get(`/student/attempts/${attemptId}/timer`),
  autosave: (attemptId, data) => api.post(`/student/attempts/${attemptId}/autosave`, data),
  submit: (attemptId, data) => api.post(`/student/attempts/${attemptId}/submit`, data),
};

// Student Authentication API
export const authAPI = {
  listBatches: () => api.get('/auth/student/batches'),
  getBatchByInvite: (inviteCode) => api.get(`/auth/student/batches/by-invite/${encodeURIComponent(inviteCode)}`),
  register: (userData) => api.post('/auth/student/register', userData),
  login: (credentials) => api.post('/auth/student/login', credentials),
  getCurrentStudent: () => api.get('/auth/student/me'),
  verifyEmail: (email, otp) => api.post('/auth/student/verify-email', { email, otp }),
  resendVerification: (email) => api.post('/auth/student/resend-verification', { email }),
  forgotPassword: (email) => api.post('/auth/student/forgot-password', { email }),
  verifyResetOtp: (email, otp) => api.post('/auth/student/verify-reset-otp', { email, otp }),
  resetPassword: (token, password) => api.post('/auth/student/reset-password', { token, password }),
};

// Submissions API
export const submissionsAPI = {
  create: (submissionData) => api.post('/submissions', submissionData),
  getAll: (params) => api.get('/submissions', { params }),
  getById: (id) => api.get(`/submissions/${id}`),
};

// Wallet API
export const walletAPI = {
  getBalance: () => api.get('/wallet/balance'),
  getTransactions: (params) => api.get('/wallet/transactions', { params }),
  getPacks: () => api.get('/wallet/packs'),
  purchasePack: (data) => api.post('/wallet/purchase', data),
  verifyPayment: (data) => api.post('/wallet/verify', data),
};

// Payment status check (reconciliation)
export const paymentAPI = {
  checkStatus: (razorpay_order_id) => api.post('/payments/check-status', { razorpay_order_id }),
};

// Admin — Orgs API
export const adminOrgsAPI = {
  create: (data) => api.post('/admin/orgs', data),
  list: () => api.get('/admin/orgs'),
  getById: (id) => api.get(`/admin/orgs/${id}`),
  update: (id, data) => api.patch(`/admin/orgs/${id}`, data),
  generateCodes: (id, data) => api.post(`/admin/orgs/${id}/codes`, data),
  listCodes: (id, params) => api.get(`/admin/orgs/${id}/codes`, { params }),
  deleteCode: (orgId, codeId) => api.delete(`/admin/orgs/${orgId}/codes/${codeId}`),
  addCredits: (id, data) => api.post(`/admin/orgs/${id}/credits`, data),
  getUsage: (id) => api.get(`/admin/orgs/${id}/usage`),
};

// Admin — Stats API
export const adminStatsAPI = {
  getLLMUsage: () => api.get('/admin/stats/llm-usage'),
  getUsers: (params) => api.get('/admin/users', { params }),
  getSubmissions: (params) => api.get('/admin/submissions', { params }),
};

// Legacy submission API (without auth)
export const legacyAPI = {
  submit: (submissionData) => api.post('/submit', submissionData),
};

// Mentor Support API
export const supportAPI = {
  contact: (data) => {
    const formData = new FormData();
    formData.append('subject', data.subject);
    formData.append('message', data.message);
    if (data.attachment) {
      formData.append('attachment', data.attachment);
    }
    return api.post('/support/contact', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export default api;

